import { parseHistoryPayload } from "./history";
import type { WorldTerm } from "./world";

export const VISUALIZATION_HISTORY_CHUNK_SIZE = 8_192n;
export const VISUALIZATION_HISTORY_MAX_CONCURRENCY = 3;
export const VISUALIZATION_HISTORY_MAX_ATTEMPTS = 3;

const DEFAULT_ENDPOINT = "/api/history";
const DEFAULT_RETRY_BASE_DELAY_MS = 250;

export interface VisualizationHistoryChunk {
  from: bigint;
  to: bigint;
  terms: readonly WorldTerm[];
}

export interface VisualizationHistoryProgress {
  from: bigint;
  through: bigint;
  totalTerms: bigint;
  totalChunks: bigint;
  loadedTerms: bigint;
  completedChunks: bigint;
}

export interface BackfillVisualizationHistoryOptions {
  from?: bigint;
  through: bigint;
  signal?: AbortSignal;
  onChunk: (
    chunk: VisualizationHistoryChunk,
  ) => void | Promise<void>;
  onProgress?: (progress: VisualizationHistoryProgress) => void;
  endpoint?: string;
  fetcher?: typeof fetch;
  concurrency?: number;
  retryBaseDelayMs?: number;
}

interface PlannedChunk {
  ordinal: bigint;
  from: bigint;
  to: bigint;
}

interface CompletedChunk {
  plan: PlannedChunk;
  chunk: VisualizationHistoryChunk;
}

/**
 * Streams one fixed history range into the visualization, newest chunk first.
 * Requests run concurrently, while callbacks remain ordered so the visible figure
 * always fills from the live edge toward n=0.
 */
export async function backfillVisualizationHistory(
  options: BackfillVisualizationHistoryOptions,
): Promise<VisualizationHistoryProgress> {
  const from = options.from ?? 0n;
  if (from < 0n) {
    throw new RangeError("history from must not be negative");
  }
  if (options.through < 0n) {
    throw new RangeError("history through must not be negative");
  }
  if (from > options.through) {
    throw new RangeError("history from must not be greater than through");
  }

  const concurrency = options.concurrency
    ?? VISUALIZATION_HISTORY_MAX_CONCURRENCY;
  if (
    !Number.isSafeInteger(concurrency)
    || concurrency < 1
    || concurrency > VISUALIZATION_HISTORY_MAX_CONCURRENCY
  ) {
    throw new RangeError(
      `history concurrency must be between 1 and ${VISUALIZATION_HISTORY_MAX_CONCURRENCY}`,
    );
  }

  const retryBaseDelayMs = options.retryBaseDelayMs
    ?? DEFAULT_RETRY_BASE_DELAY_MS;
  if (!Number.isSafeInteger(retryBaseDelayMs) || retryBaseDelayMs < 0) {
    throw new RangeError("history retry delay must be a non-negative safe integer");
  }

  const totalTerms = options.through - from + 1n;
  const totalChunks = divideRoundingUp(
    totalTerms,
    VISUALIZATION_HISTORY_CHUNK_SIZE,
  );
  let loadedTerms = 0n;
  let completedChunks = 0n;
  let nextToPlan = (
    options.through / VISUALIZATION_HISTORY_CHUNK_SIZE
  ) * VISUALIZATION_HISTORY_CHUNK_SIZE;
  let nextOrdinal = 0n;
  let nextToEmit = 0n;

  const controller = new AbortController();
  const unlinkParentSignal = linkAbortSignal(options.signal, controller);
  const signal = controller.signal;
  // Some browsers require the native fetch function to keep its global receiver.
  const fetcher: typeof fetch = options.fetcher
    ?? ((input, init) => globalThis.fetch(input, init));
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const inFlight = new Map<bigint, Promise<CompletedChunk>>();
  const ready = new Map<bigint, CompletedChunk>();

  const progress = (): VisualizationHistoryProgress => ({
    from,
    through: options.through,
    totalTerms,
    totalChunks,
    loadedTerms,
    completedChunks,
  });

  const claimChunk = (): PlannedChunk | null => {
    if (nextToPlan + VISUALIZATION_HISTORY_CHUNK_SIZE - 1n < from) return null;
    const chunkFrom = nextToPlan < from ? from : nextToPlan;
    const chunkEnd = nextToPlan + VISUALIZATION_HISTORY_CHUNK_SIZE - 1n;
    const to = chunkEnd < options.through ? chunkEnd : options.through;
    const plan = { ordinal: nextOrdinal, from: chunkFrom, to };
    nextOrdinal += 1n;
    nextToPlan -= VISUALIZATION_HISTORY_CHUNK_SIZE;
    return plan;
  };

  const fillSlots = (): void => {
    while (inFlight.size + ready.size < concurrency) {
      const plan = claimChunk();
      if (plan === null) return;
      const request = fetchHistoryChunk({
        endpoint,
        fetcher,
        fixedThrough: options.through,
        plan,
        retryBaseDelayMs,
        signal,
      }).then((chunk) => ({ plan, chunk }));
      inFlight.set(plan.ordinal, request);
    }
  };

  try {
    throwIfAborted(signal);
    options.onProgress?.(progress());
    fillSlots();

    while (inFlight.size > 0) {
      const completed = await Promise.race(inFlight.values());
      inFlight.delete(completed.plan.ordinal);
      ready.set(completed.plan.ordinal, completed);

      while (true) {
        const next = ready.get(nextToEmit);
        if (next === undefined) break;
        ready.delete(nextToEmit);
        throwIfAborted(signal);
        await options.onChunk(next.chunk);
        loadedTerms += BigInt(next.chunk.terms.length);
        completedChunks += 1n;
        nextToEmit += 1n;
        options.onProgress?.(progress());
      }

      fillSlots();
    }

    if (ready.size > 0 || completedChunks !== totalChunks) {
      throw new Error("history backfill finished with incomplete chunks");
    }
    return progress();
  } catch (error) {
    controller.abort(error);
    await Promise.allSettled(inFlight.values());
    throw error;
  } finally {
    unlinkParentSignal();
  }
}

interface FetchHistoryChunkOptions {
  endpoint: string;
  fetcher: typeof fetch;
  fixedThrough: bigint;
  plan: PlannedChunk;
  retryBaseDelayMs: number;
  signal: AbortSignal;
}

async function fetchHistoryChunk(
  options: FetchHistoryChunkOptions,
): Promise<VisualizationHistoryChunk> {
  let lastError: unknown;
  for (
    let attempt = 1;
    attempt <= VISUALIZATION_HISTORY_MAX_ATTEMPTS;
    attempt += 1
  ) {
    throwIfAborted(options.signal);
    try {
      return await requestHistoryChunk(options);
    } catch (error) {
      if (options.signal.aborted) throw abortReason(options.signal);
      lastError = error;
      if (attempt === VISUALIZATION_HISTORY_MAX_ATTEMPTS) break;
      const delayMs = options.retryBaseDelayMs * (2 ** (attempt - 1));
      await abortableDelay(delayMs, options.signal);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("history request failed after retries");
}

async function requestHistoryChunk(
  options: FetchHistoryChunkOptions,
): Promise<VisualizationHistoryChunk> {
  const query = new URLSearchParams({
    from: options.plan.from.toString(),
    limit: (options.plan.to - options.plan.from + 1n).toString(),
    through: options.fixedThrough.toString(),
  });
  const separator = options.endpoint.includes("?") ? "&" : "?";
  const response = await options.fetcher(
    `${options.endpoint}${separator}${query}`,
    {
      signal: options.signal,
      headers: { Accept: "application/json" },
    },
  );
  if (!response.ok) {
    throw new Error(`history request failed (${response.status})`);
  }

  const payload = parseHistoryPayload(await response.json());
  const expectedNextFrom = options.plan.to < options.fixedThrough
    ? (options.plan.to + 1n).toString()
    : null;
  const expectedLength = Number(options.plan.to - options.plan.from + 1n);

  if (payload.from !== options.plan.from.toString()) {
    throw new Error("history response started at an unexpected term");
  }
  if (payload.through !== options.fixedThrough.toString()) {
    throw new Error("history response changed the fixed through value");
  }
  if (payload.nextFrom !== expectedNextFrom) {
    throw new Error("history response ended at an unexpected term");
  }
  if (payload.terms.length !== expectedLength) {
    throw new Error(
      `history response contained ${payload.terms.length} terms; expected ${expectedLength}`,
    );
  }
  if (
    payload.terms[0]?.n !== options.plan.from.toString()
    || payload.terms.at(-1)?.n !== options.plan.to.toString()
  ) {
    throw new Error("history response did not cover the requested chunk");
  }

  return {
    from: options.plan.from,
    to: options.plan.to,
    terms: payload.terms,
  };
}

function divideRoundingUp(value: bigint, divisor: bigint): bigint {
  return (value + divisor - 1n) / divisor;
}

function linkAbortSignal(
  parent: AbortSignal | undefined,
  controller: AbortController,
): () => void {
  if (parent === undefined) return () => undefined;
  const abort = (): void => controller.abort(parent.reason);
  if (parent.aborted) {
    abort();
    return () => undefined;
  }
  parent.addEventListener("abort", abort, { once: true });
  return () => parent.removeEventListener("abort", abort);
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException("The operation was aborted", "AbortError");
}

function throwIfAborted(signal: AbortSignal): void {
  if (signal.aborted) throw abortReason(signal);
}

function abortableDelay(delayMs: number, signal: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  if (delayMs === 0) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", abort);
      resolve();
    }, delayMs);
    const abort = (): void => {
      clearTimeout(timer);
      reject(abortReason(signal));
    };
    signal.addEventListener("abort", abort, { once: true });
  });
}
